// src/pages/CursoPage.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
// Iconos SOLID
import { LockClosedIcon, ChevronDownIcon, CheckCircleIcon, SparklesIcon, WrenchScrewdriverIcon, VideoCameraIcon } from '@heroicons/react/24/solid';
// Iconos OUTLINE
import { PlayCircleIcon, QueueListIcon, LightBulbIcon } from '@heroicons/react/24/outline';
// Importa los datos desde el archivo central
import { courseInfo, allActivities } from '../../data/es/CourseStructure';
import UrlConfig from '../../data/UrlConfig'; // <-- IMPORTADO

const PREMIUM_ROLE_ID = 7;

/**
 * Comprueba si una URL es de YouTube y la convierte a formato "embed".
 * @param {string} url La URL del video.
 * @returns {string|null} La URL "embed" de YouTube o null si no es un enlace de YouTube.
 */
const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    let videoId = null;
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|embed\/|v\/)|youtu\.be\/)([^&?#]+)/;
    const match = url.match(youtubeRegex);

    if (match && match[1]) {
        videoId = match[1];
    } else {
        return null; // No es una URL de YouTube reconocible
    }

    return `https://www.youtube.com/embed/${videoId}`;
};

const renderActivityIcon = (activity, isCompleted, isLockedByRole, isLockedByProgression, isNextStep) => {
  if (isLockedByRole && (activity.isTool || activity.isTheory || activity.isCoaching)) {
    let MainIcon;
    let iconColor;

    if (activity.isTool) {
      MainIcon = WrenchScrewdriverIcon;
      iconColor = 'text-cyan-400';
    } else if (activity.isTheory) {
      MainIcon = LightBulbIcon;
      iconColor = 'text-indigo-400';
    } else { // Si no es herramienta ni teoría, es coaching
      MainIcon = SparklesIcon;
      iconColor = 'text-purple-400';
    }

    return (
      <div className="relative w-6 h-6 flex-shrink-0">
        <MainIcon className={`w-full h-full ${iconColor} opacity-40`} />
        <LockClosedIcon className="absolute -bottom-1 -right-1 w-4 h-4 text-yellow-500 bg-gray-900 rounded-full p-0.5" />
      </div>
    );
  }

  if (isCompleted) return <CheckCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0" />;
  if (isLockedByRole) return <LockClosedIcon className="w-6 h-6 text-yellow-600 flex-shrink-0" />;
  if (isNextStep && !activity.isIgnorable) return <PlayCircleIcon className="w-6 h-6 text-green-400 flex-shrink-0" />;
  if (activity.isTheory) return <LightBulbIcon className="w-6 h-6 text-indigo-400 flex-shrink-0" />;
  if (activity.isTool) return <WrenchScrewdriverIcon className="w-6 h-6 text-cyan-400 flex-shrink-0" />;
  if (activity.isCoaching) return <SparklesIcon className="w-6 h-6 text-purple-400 flex-shrink-0" />;
  if (isLockedByProgression) return <QueueListIcon className="w-6 h-6 text-blue-500 flex-shrink-0" />;
  return <div className="w-6 h-6 flex-shrink-0"></div>;
};


const CursoPage = () => {
    const [expandedModule, setExpandedModule] = useState(null);
    const { user, guestProgress } = useAuth();
    const [completedActivities, setCompletedActivities] = useState(new Set());
    const [showSavePrompt, setShowSavePrompt] = useState(false);
    const [activeVideo, setActiveVideo] = useState(null);
  
    useEffect(() => {
        const processCompletionData = (completedSet) => {
            setCompletedActivities(completedSet);
            const firstUncompleted = allActivities.find(act => !completedSet.has(act.slug) && !act.isIgnorable);
            
            if (firstUncompleted) {
                setExpandedModule(firstUncompleted.module);
            } else if (courseInfo.modules.length > 0) {
                setExpandedModule(courseInfo.modules[courseInfo.modules.length - 1].module);
            } else {
                setExpandedModule(1);
            }
        };

        if (user) {
            const fetchCompletionStatus = async () => {
                try {
                    // <-- MODIFICADO: Se usa UrlConfig
                    const response = await fetch(UrlConfig.activity_getCompletionStatus, { credentials: 'include' });
                    if (response.ok) {
                        const result = await response.json();
                        if (result.success) {
                            processCompletionData(new Set(result.data));
                        }
                    }
                } catch (error) { 
                    console.error("Error fetching completion status:", error);
                    processCompletionData(new Set());
                }
            };
            fetchCompletionStatus();
        } else {
            processCompletionData(guestProgress.completedSlugs);
        }
    }, [user, guestProgress]);
  
    useEffect(() => {
      document.body.className = 'bg-black text-white';
      document.title = 'Programa Manifiesta | Tu Viaje Interior';
    }, []);
  
    const handleGuestClickOnPremium = () => setShowSavePrompt(true);

    const handlePlayVideoInModal = (e, video) => {
        e.stopPropagation();
        e.preventDefault();
        setActiveVideo(video);
    };

    const firstUncompletedActivity = allActivities.find(act => !completedActivities.has(act.slug) && !act.isIgnorable);
    const nextStepLabelText = !completedActivities.size ? 'EMPIEZA AQUÍ' : 'SIGUE AQUÍ';

    const isNextStepLocked = firstUncompletedActivity && !firstUncompletedActivity.isFree && !(user?.roles?.includes(PREMIUM_ROLE_ID));

    const modalEmbedUrl = activeVideo ? getYouTubeEmbedUrl(activeVideo.url) : null;
    const nextStepEmbedUrl = firstUncompletedActivity?.videoUrl ? getYouTubeEmbedUrl(firstUncompletedActivity.videoUrl) : null;


    return (
        <div className="bg-black text-white min-h-screen font-sans">
            {activeVideo && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4 animate-fade-in"
                    onClick={() => setActiveVideo(null)}
                >
                    <div 
                        className="bg-gray-900 rounded-2xl w-full max-w-4xl shadow-2xl shadow-yellow-500/10 relative"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-4 border-b border-gray-800 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-yellow-400">{activeVideo.title}</h3>
                            <button onClick={() => setActiveVideo(null)} className="text-gray-500 hover:text-white text-2xl leading-none">×</button>
                        </div>
                        <div className="p-2 sm:p-4 bg-black aspect-video">
                            {modalEmbedUrl ? (
                                <iframe
                                    className="w-full h-full rounded-lg"
                                    src={`${modalEmbedUrl}?autoplay=1&rel=0`}
                                    title={activeVideo.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            ) : (
                                <video 
                                    className="w-full h-full max-h-[80vh] rounded-lg" 
                                    src={activeVideo.url} 
                                    controls autoPlay onEnded={() => setActiveVideo(null)}
                                >
                                    Tu navegador no soporta el tag de video.
                                </video>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showSavePrompt && (
                <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="bg-gray-900 border border-yellow-500/30 rounded-2xl p-8 max-w-lg text-center shadow-2xl shadow-yellow-500/10 relative">
                        <button onClick={() => setShowSavePrompt(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white text-2xl leading-none">×</button>
                        <h2 className="text-3xl font-bold text-yellow-400 mb-4">¡Vas por muy buen camino!</h2>
                        <p className="text-gray-300 mb-6">Para guardar tu progreso y desbloquear el resto del curso, crea una cuenta o inicia sesión.</p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link to="/signup?redirect=/cursos/manifiesta" className="w-full sm:w-auto bg-yellow-500 text-black font-bold py-3 px-8 rounded-lg hover:bg-yellow-400 transition-transform transform hover:scale-105">Crear Cuenta Gratis</Link>
                            <Link to="/login?redirect=/cursos/manifiesta" className="w-full sm:w-auto bg-gray-700 text-white font-bold py-3 px-8 rounded-lg hover:bg-gray-600">Ya tengo cuenta</Link>
                        </div>
                    </div>
                </div>
            )}
            
            <header className="py-12 text-center">
                <h1 className="text-4xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-200 via-yellow-400 to-yellow-600 mb-3">
                    {courseInfo.welcomeTitle(user ? user.username : 'viajero')}
                </h1>
                <p className="text-lg text-gray-300 font-light max-w-3xl mx-auto px-4">
                    {courseInfo.welcomeSubtitle} {!user && <strong className="font-semibold text-gray-200">Comienza totalmente gratis.</strong>}
                </p>
            </header>
    
            <main className="max-w-4xl mx-auto px-4 pb-20">
                
                {firstUncompletedActivity && firstUncompletedActivity.videoUrl && !isNextStepLocked && (
                    <div className="mb-12 bg-gray-900/50 border border-yellow-500/30 rounded-2xl p-4 sm:p-6 shadow-lg shadow-yellow-500/5">
                        <div className="flex items-center gap-2">
                           <SparklesIcon className="w-6 h-6 text-yellow-400" />
                           <h2 className="text-sm font-bold text-yellow-400 tracking-wider">{nextStepLabelText}</h2>
                        </div>
                        <h3 className="text-2xl sm:text-3xl font-bold text-white mt-2">{firstUncompletedActivity.title}</h3>
                        <p className="text-gray-300 mt-2 text-base">{firstUncompletedActivity.description}</p>
                        
                        <div className="mt-4 aspect-video bg-black rounded-lg border border-gray-700">
                           {nextStepEmbedUrl ? (
                                <iframe
                                    className="w-full h-full rounded-lg"
                                    src={`${nextStepEmbedUrl}?autoplay=1&mute=1&rel=0`}
                                    title={firstUncompletedActivity.title}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                           ) : (
                               <video 
                                   className="w-full h-full rounded-lg" 
                                   src={firstUncompletedActivity.videoUrl} 
                                   controls
                                   autoPlay
                                   muted
                               >
                                   Tu navegador no soporta el tag de video.
                               </video>
                           )}
                        </div>
                        
                        <Link to={`/cursos/manifiesta/${firstUncompletedActivity.slug}`} className="mt-4 inline-block w-full sm:w-auto text-center bg-yellow-500 text-black font-bold py-3 px-6 rounded-lg hover:bg-yellow-400 transition-transform transform hover:scale-105">
                            Comenzar Actividad →
                        </Link>
                    </div>
                )}
                
                <div className="space-y-6">
                    {courseInfo.modules.map((module) => {
                        const isExpanded = expandedModule === module.module;
                        return (
                            <div key={module.module} className="bg-gray-900/50 border border-yellow-500/20 rounded-xl overflow-hidden transition-all duration-300">
                                <button onClick={() => setExpandedModule(isExpanded ? null : module.module)} className="w-full flex justify-between items-center p-4 sm:p-6 text-left hover:bg-gray-900/60 transition-colors duration-200">
                                    <div className="flex items-center gap-4 sm:gap-6">
                                        <img src={module.landingPageImage} alt={module.title} className="w-24 h-16 sm:w-32 sm:h-20 object-cover rounded-lg shadow-lg shadow-black/30" />
                                        <div>
                                            <p className="text-sm font-bold text-yellow-400 tracking-wider">MÓDULO {module.module}</p>
                                            <h2 className="text-xl sm:text-2xl font-bold text-white mt-1">{module.title}</h2>
                                        </div>
                                    </div>
                                    <ChevronDownIcon className={`w-6 h-6 text-yellow-400 transition-transform duration-500 flex-shrink-0 ml-4 ${isExpanded ? 'rotate-180' : ''}`} />
                                </button>
            
                                <div className={`transition-all duration-500 ease-in-out overflow-hidden ${isExpanded ? 'max-h-screen' : 'max-h-0'}`}>
                                    <div className="bg-black/20 pt-2 pb-4 px-2 sm:px-4">
                                        {module.activities.map((activity) => {
                                            const isCompleted = completedActivities.has(activity.slug);
                                            const hasPremiumAccess = user?.roles?.includes(PREMIUM_ROLE_ID);
                                            const isLockedByRole = !activity.isFree && !hasPremiumAccess;
                                            const globalIndex = allActivities.findIndex(a => a.id === activity.id);
                                            let isLockedByProgression = false;
                                            if (globalIndex > 0) {
                                                let prerequisite = null;
                                                for (let i = globalIndex - 1; i >= 0; i--) {
                                                    if (!allActivities[i].isIgnorable) { prerequisite = allActivities[i]; break; }
                                                }
                                                if (prerequisite && !completedActivities.has(prerequisite.slug)) isLockedByProgression = true;
                                            }
                                            const isNextStep = firstUncompletedActivity && activity.id === firstUncompletedActivity.id;
                                            const isLocked = isLockedByRole || isLockedByProgression;

                                            const activityRowClasses = [
                                                "flex items-center justify-between p-4 sm:p-5 text-left w-full relative group rounded-lg",
                                                isNextStep && !isLocked ? "bg-yellow-500/10 animate-pulse sm:bg-transparent sm:animate-none" : ""
                                            ].join(" ");

                                            const activityElement = (
                                                <div className={activityRowClasses}>
                                                    <div className="flex items-center gap-4 flex-grow min-w-0">
                                                        {renderActivityIcon(activity, isCompleted, isLockedByRole, isLockedByProgression, isNextStep)}
                                                        <span className={`text-base sm:text-lg truncate ${isCompleted ? 'text-gray-500' : 'text-gray-200'} ${isLocked ? 'opacity-60' : ''}`}>{activity.title}</span>
                                                    </div>
                                                    
                                                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                                        {!isLocked && activity.videoUrl && (
                                                            <button 
                                                                onClick={(e) => handlePlayVideoInModal(e, { url: activity.videoUrl, title: activity.title })}
                                                                className="p-1.5 rounded-full hover:bg-yellow-500/20 text-yellow-400 transition-colors z-10"
                                                                aria-label={`Ver video de ${activity.title}`}
                                                            >
                                                                <VideoCameraIcon className="w-6 h-6"/>
                                                            </button>
                                                        )}

                                                        {isNextStep && !isLocked && (
                                                            <div className="hidden sm:flex items-center gap-1.5 bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-[11px] font-semibold tracking-wider px-2.5 py-1 rounded-full animate-pulse">
                                                                <SparklesIcon className="w-3.5 h-3.5" />
                                                                <span>{nextStepLabelText}</span>
                                                            </div>
                                                        )}
                                                    </div>

                                                    {isLocked && (
                                                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-max px-3 py-1.5 bg-gray-800 text-white text-xs font-medium rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none z-10">
                                                            {isLockedByRole ? 'Contenido Premium' : 'Completa la actividad anterior'}
                                                        </span>
                                                    )}
                                                </div>
                                            );

                                            return (
                                                <div key={activity.id} className="border-t border-gray-800/50 first:border-t-0">
                                                    {isLocked ? (
                                                        <button onClick={!user && isLockedByRole ? handleGuestClickOnPremium : undefined} className="w-full cursor-default">{activityElement}</button>
                                                    ) : (
                                                        <Link to={`/cursos/manifiesta/${activity.slug}`} className="block w-full hover:bg-gray-800/50 transition-colors rounded-lg">{activityElement}</Link>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </main>
        </div>
    );
};

export default CursoPage;